import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Star, MessageSquare, TrendingUp, Send } from 'lucide-react';
import { colors } from '../theme';

const Feedbacks = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [mensagem, setMensagem] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('primaza_token');
      const [feedbacksRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/feedbacks`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/feedbacks/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setFeedbacks(feedbacksRes.data.feedbacks);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const enviarPesquisas = async () => {
    setEnviando(true);
    setMensagem('');
    
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(
        `${API}/admin/enviar-pesquisas`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMensagem(response.data.message);
      setTimeout(() => setMensagem(''), 5000);
    } catch (error) {
      setMensagem('Erro ao enviar pesquisas');
    } finally {
      setEnviando(false);
    }
  };

  const renderEstrelas = (valor) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            size={20}
            fill={i <= valor ? '#FCD34D' : 'none'}
            stroke={i <= valor ? '#FCD34D' : '#D1D5DB'}
          />
        ))}
      </div>
    );
  };

  const getCorMedia = (valor) => {
    if (valor >= 4.5) return '#10B981'; // Verde
    if (valor >= 3.5) return '#F59E0B'; // Amarelo
    return '#EF4444'; // Vermelho
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: colors.primaza.primary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="h-8 w-8" style={{ color: '#FCD34D' }} />
            <div>
              <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
                Feedbacks dos Clientes
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Pesquisas de satisfação recebidas
              </p>
            </div>
          </div>
          
          <Button
            onClick={enviarPesquisas}
            disabled={enviando}
            style={{ backgroundColor: colors.primaza.primary }}
          >
            {enviando ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar Pesquisas
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Mensagem */}
        {mensagem && (
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">
              ✅ {mensagem}
            </AlertDescription>
          </Alert>
        )}

        {/* Estatísticas */}
        {stats && stats.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2" style={{ color: colors.primaza.primary }} />
                  <p className="text-sm text-gray-600">Média Geral</p>
                  <p className="text-3xl font-bold mt-1" style={{ color: getCorMedia(stats.media_geral) }}>
                    {stats.media_geral}
                  </p>
                  <div className="flex justify-center mt-2">
                    {renderEstrelas(Math.round(stats.media_geral))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Produtos</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: getCorMedia(stats.media_produtos) }}>
                    {stats.media_produtos}
                  </p>
                  <div className="flex justify-center mt-2 scale-75">
                    {renderEstrelas(Math.round(stats.media_produtos))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Atendimento</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: getCorMedia(stats.media_atendimento) }}>
                    {stats.media_atendimento}
                  </p>
                  <div className="flex justify-center mt-2 scale-75">
                    {renderEstrelas(Math.round(stats.media_atendimento))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Prazo</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: getCorMedia(stats.media_prazo) }}>
                    {stats.media_prazo}
                  </p>
                  <div className="flex justify-center mt-2 scale-75">
                    {renderEstrelas(Math.round(stats.media_prazo))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Embalagem</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: getCorMedia(stats.media_embalagem) }}>
                    {stats.media_embalagem}
                  </p>
                  <div className="flex justify-center mt-2 scale-75">
                    {renderEstrelas(Math.round(stats.media_embalagem))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Lista de Feedbacks */}
        <Card>
          <CardHeader>
            <CardTitle>
              Todos os Feedbacks ({feedbacks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {feedbacks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Star size={48} className="mx-auto mb-4 opacity-20" />
                <p>Nenhum feedback recebido ainda</p>
                <p className="text-sm mt-2">
                  Os feedbacks aparecerão aqui quando os clientes responderem às pesquisas
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {feedbacks.map((feedback) => (
                  <div key={feedback.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-lg">{feedback.nome_cliente}</p>
                        <p className="text-sm text-gray-600">
                          Pedido #{feedback.pedido_numero} • {new Date(feedback.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: getCorMedia(
                          (feedback.avaliacao_produtos + feedback.avaliacao_atendimento + 
                           feedback.avaliacao_prazo + feedback.avaliacao_embalagem) / 4
                        )}}>
                          {((feedback.avaliacao_produtos + feedback.avaliacao_atendimento + 
                             feedback.avaliacao_prazo + feedback.avaliacao_embalagem) / 4).toFixed(1)}
                        </p>
                        <p className="text-xs text-gray-500">Média</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Produtos</p>
                        {renderEstrelas(feedback.avaliacao_produtos)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Atendimento</p>
                        {renderEstrelas(feedback.avaliacao_atendimento)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Prazo</p>
                        {renderEstrelas(feedback.avaliacao_prazo)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Embalagem</p>
                        {renderEstrelas(feedback.avaliacao_embalagem)}
                      </div>
                    </div>

                    {feedback.comentario && (
                      <div className="bg-gray-50 rounded p-3 mt-3">
                        <div className="flex items-start gap-2">
                          <MessageSquare size={16} className="text-gray-400 mt-1" />
                          <p className="text-sm text-gray-700 italic">"{feedback.comentario}"</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default Feedbacks;
